import { html, render } from "lit";
import { _APP_DATA } from "../../data/AppData";
import { DataUtilLocalStorage } from "../../data/utils/LocalStorage";
import "../../../styles/components/PrivacyPolicyModal.scss";

const PRIVACY_ACCEPTANCE_STORAGE_KEY = "privacy_accepted";

export class PrivacyPolicyModal {
    private readonly root: HTMLDivElement;
    private readonly onClose: (() => void) | null;
    private readonly privacyUrl = "https://web2.kidi.mx/teacher/avisodeprivacidad.html";
    private privacyLoadState: "loading" | "ready" | "error" = "loading";
    private privacyRetryKey = 0;
    private consentChecked = false;
    private privacyLoadTimeout: number | null = null;

    public static IsAccepted(): boolean {
        try {
            return DataUtilLocalStorage._GetPreference(PRIVACY_ACCEPTANCE_STORAGE_KEY) === "true";
        } catch {
            return false;
        }
    }

    public static ShouldShowForActiveSession(): boolean {
        return !!_APP_DATA?.userData && !PrivacyPolicyModal.IsAccepted();
    }

    public static ShowIfNeeded(): boolean {
        if (!PrivacyPolicyModal.ShouldShowForActiveSession()) return false;
        if (document.querySelector(".privacy-modal-backdrop")) return false;

        new PrivacyPolicyModal();
        return true;
    }

    constructor(onClose?: () => void) {
        this.onClose = onClose ?? null;
        this.root = document.createElement("div");
        this.root.className = "privacy-modal-backdrop";
        this.root.addEventListener("click", (event: Event) => {
            event.stopPropagation();
            if (!PrivacyPolicyModal.IsAccepted()) return;
            this.dispose();
        });

        document.body.appendChild(this.root);
        this.open();
    }

    public dispose() {
        if (this.privacyLoadTimeout) {
            window.clearTimeout(this.privacyLoadTimeout);
            this.privacyLoadTimeout = null;
        }

        this.root.remove();
        this.onClose?.();
    }

    public close() {
        if (!PrivacyPolicyModal.IsAccepted()) return;
        this.dispose();
    }

    private open() {
        this.privacyLoadState = "loading";
        this.privacyRetryKey += 1;
        this.render();

        if (this.privacyLoadTimeout) {
            window.clearTimeout(this.privacyLoadTimeout);
        }

        this.privacyLoadTimeout = window.setTimeout(() => {
            if (this.privacyLoadState === "loading") {
                this.privacyLoadState = "error";
                this.render();
            }
        }, 15000);
    }

    private retry() {
        this.privacyLoadState = "loading";
        this.privacyRetryKey += 1;
        this.render();

        if (this.privacyLoadTimeout) {
            window.clearTimeout(this.privacyLoadTimeout);
        }

        this.privacyLoadTimeout = window.setTimeout(() => {
            if (this.privacyLoadState === "loading") {
                this.privacyLoadState = "error";
                this.render();
            }
        }, 15000);
    }

    private onLoaded = () => {
        this.privacyLoadState = "ready";
        if (this.privacyLoadTimeout) {
            window.clearTimeout(this.privacyLoadTimeout);
            this.privacyLoadTimeout = null;
        }
        this.render();
    }

    private onFailed = () => {
        this.privacyLoadState = "error";
        if (this.privacyLoadTimeout) {
            window.clearTimeout(this.privacyLoadTimeout);
            this.privacyLoadTimeout = null;
        }
        this.render();
    }

    private onConsentChange = (event: Event) => {
        const input = event.target as HTMLInputElement | null;
        this.consentChecked = Boolean(input?.checked);
        this.render();
    }

    private acceptPrivacy = () => {
        if (!this.consentChecked) return;
        try {
            DataUtilLocalStorage._SetPreference(PRIVACY_ACCEPTANCE_STORAGE_KEY, true);
        } catch {
            // Ignored: storage may be unavailable in some restricted environments.
        }

        this.dispose();
    }

    private render() {
        const isAccepted = PrivacyPolicyModal.IsAccepted();

        render(html`
            <div class="privacy-modal" @click=${(event: Event) => event.stopPropagation()}>
                ${!isAccepted ? "" : html`
                    <button
                        type="button"
                        class="privacy-modal-close"
                        aria-label="Cerrar política de privacidad"
                        @click=${(event: Event) => {
                    event.stopPropagation();
                    this.dispose();
                }}
                    >
                        ×
                    </button>
                `}

                <div class="privacy-modal-header">
                    <h3>Política de privacidad</h3>
                </div>

                ${this.privacyLoadState === "error" ? html`
                    <div class="privacy-error">
                        <p>No se pudo cargar la política de privacidad.</p>
                        <button
                            type="button"
                            class="privacy-retry-btn"
                            @click=${(event: Event) => {
                    event.stopPropagation();
                    this.retry();
                }}
                        >
                            Recargar
                        </button>
                    </div>
                ` : html`
                    <div class="privacy-frame-wrap">
                        ${this.privacyLoadState === "loading" ? html`
                            <div class="privacy-loading">Cargando política...</div>
                        ` : ""}
                        <iframe
                            key=${this.privacyRetryKey}
                            class="privacy-iframe"
                            src=${this.privacyUrl}
                            title="Política de privacidad"
                            loading="lazy"
                            @load=${this.onLoaded}
                            @error=${this.onFailed}
                        ></iframe>
                    </div>
                `}

                ${!isAccepted ? html`
                    <div class="privacy-consent-box">
                        <label class="privacy-consent">
                            <input
                                type="checkbox"
                                .checked=${this.consentChecked}
                                @change=${this.onConsentChange}
                            />
                            <span>He leído y estoy de acuerdo con el aviso de privacidad</span>
                        </label>
                        <button
                            type="button"
                            class="privacy-accept-btn"
                            ?disabled=${!this.consentChecked}
                            @click=${(event: Event) => {
                    event.stopPropagation();
                    this.acceptPrivacy();
                }}
                        >
                            Aceptar
                        </button>
                    </div>
                ` : ""}
            </div>
        `, this.root);
    }
}
